import { useEffect, useState } from 'react';

import type { CreatorDiscoveryEmailResource } from '@frontend/app/hooks';

const SELECTED_EMAIL_RESOURCE_KEY = 'aspire.creatorDiscovery.selectedEmailResourceId';

interface UseSelectedEmailResourceProps {
  emailResources: CreatorDiscoveryEmailResource[];
}

export const useSelectedEmailResource = ({ emailResources }: UseSelectedEmailResourceProps) => {
  const [selectedEmailResource, setSelectedEmailResource] = useState<CreatorDiscoveryEmailResource | undefined>(
    undefined,
  );

  // Set default to first email resource when data loads, or use saved from localStorage
  useEffect(() => {
    if (emailResources.length > 0 && !selectedEmailResource) {
      // Try to get saved ID from localStorage
      try {
        const savedId = localStorage.getItem(SELECTED_EMAIL_RESOURCE_KEY);

        if (savedId) {
          // Find the resource with the saved ID (convert string back to number)
          const parsedId = parseInt(savedId, 10);
          if (!isNaN(parsedId)) {
            const savedResource = emailResources.find((resource) => resource.id === parsedId);
            if (savedResource) {
              setSelectedEmailResource(savedResource);
              return;
            }
          }
        }
      } catch (error) {
        console.warn('Failed to read from localStorage:', error);
      }

      // Fall back to first resource if no saved ID or resource not found
      setSelectedEmailResource(emailResources[0]);
    }
  }, [emailResources, selectedEmailResource]);

  // Save selected email resource ID to localStorage when it changes
  useEffect(() => {
    if (selectedEmailResource) {
      try {
        localStorage.setItem(SELECTED_EMAIL_RESOURCE_KEY, selectedEmailResource.id.toString());
      } catch (error) {
        console.warn('Failed to save to localStorage:', error);
      }
    }
  }, [selectedEmailResource]);

  return {
    selectedEmailResource,
    setSelectedEmailResource,
  };
};
